import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiResponse } from '../../../../models/base';
import { AuthToken, AuthUser, User } from '../../../../models/user';
import { environment } from '../../../../../environments/environment';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../../../store/auth/auth.actions';
import * as AuthSelectors from '../../../../store/auth/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  protected apiUrl = environment.apiUrl;
  private http = inject(HttpClient);
  private store = inject(Store);

  user$ = this.store.select(AuthSelectors.selectUser);
  isAuthenticated$ = this.store.select(AuthSelectors.selectIsAuthenticated);
  userRoles$ = this.store.select(AuthSelectors.selectUserRoles);
  userPermissions$ = this.store.select(AuthSelectors.selectUserPermissions);

  initFromStorage() {
    const user = this.getUserInfo();
    const token = this.getAccessToken();

    if (user && token) {
      this.store.dispatch(AuthActions.loginSuccess({ user, token, fromApi: false }));
    } else {
      console.log('[AuthService] no auth data found in storage');
    }
  }

  login(identifier: string, password: string): Observable<ApiResponse<AuthUser>> {
    return this.http.post<ApiResponse<AuthUser>>(
      `${this.apiUrl}/auth/login`,
      { identifier, password },
      {
        withCredentials: true,
      }
    );
  }

  refreshToken(): Observable<ApiResponse<AuthToken>> {
    return this.http.post<ApiResponse<AuthToken>>(
      `${this.apiUrl}/auth/refresh`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  register(email: string, password: string, name: string): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/auth/register`, { email, password, name });
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/reset-password`, {
      token,
      password,
    });
  }

  verifyOtp(email: string, otp: string): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/auth/verify-otp`, { email, otp });
  }

  logout(): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/auth/logout`,{});
  }

  isLoggedIn() {
    return !!this.getAccessToken();
  }

  getAccessToken(): AuthToken | null {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(token) : null;
  }

  setAccessToken(token: AuthToken) {
    localStorage.setItem('token', JSON.stringify(token));
  }

  getUserInfo() {
    const authInfo = localStorage.getItem('authInfo');
    if (!authInfo) return null;
    try {
      return JSON.parse(authInfo) as User;
    } catch (e) {
      console.error('Erreur de parsing du user localStorage:', e);
      return null;
    }
  }

  getUserRoles(): string[] {
    const user = this.getUserInfo();
    return user?.roles || [];
  }

  getUserPermissions(): string[] {
    const user = this.getUserInfo();
    return user?.permissions || [];
  }

  setUserInfoToken(user: User) {
    localStorage.setItem('authInfo', JSON.stringify(user));
  }

  clearAuth(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('authInfo');
  }

  hasRole(role: string): boolean {
    let hasRole = false;
    this.store
      .select(AuthSelectors.selectHasRole(role))
      .subscribe((result) => (hasRole = result))
      .unsubscribe();
    return hasRole;
  }

  hasAnyRole(roles: string[]): boolean {
    let hasAnyRole = false;
    this.store
      .select(AuthSelectors.selectHasAnyRole(roles))
      .subscribe((result) => (hasAnyRole = result))
      .unsubscribe();
    return hasAnyRole;
  }

  hasPermission(permission: string): boolean {
    let hasPermission = false;
    this.store
      .select(AuthSelectors.selectHasPermission(permission))
      .subscribe((result) => (hasPermission = result))
      .unsubscribe();
    return hasPermission;
  }

  hasAnyPermission(permissions: string[]): boolean {
    let hasAnyPermission = false;
    this.store
      .select(AuthSelectors.selectHasAnyPermission(permissions))
      .subscribe((result) => (hasAnyPermission = result))
      .unsubscribe();
    return hasAnyPermission;
  }

  hasAllPermissions(permissions: string[]): boolean {
    let hasAllPermissions = false;
    this.store
      .select(AuthSelectors.selectHasAllPermissions(permissions))
      .subscribe((result) => (hasAllPermissions = result))
      .unsubscribe();
    return hasAllPermissions;
  }
}
